import { useMemo } from 'react';

import { ExcelStyle } from 'ag-grid-community';
import { TFunction } from 'i18next';

import { useFeatureFlag } from '@/app/services/features';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown/constants';
import { StringMapLabel } from '@/components/common/string-map-label';

import { PartnershipCellRenderer } from '../components/PartnershipCell';
import { formatPartnership } from './partnership';

export interface ColumnDef {
  field: string;
  headerName: string;
  width?: number;
  rowDrag?: boolean;
  hide?: boolean;
  flex?: number;
  valueGetter?: (p: any) => string | number;
  valueFormatter?: (p: any) => string;
  headerClass?: string;
  filterValueGetter?: (p: any) => string | number;
  cellRenderer?: any;
  cellRendererParams?: any;
  checkboxSelection?: boolean;
  headerCheckboxSelection?: boolean;
  headerCheckboxSelectionFilteredOnly?: boolean;
}

const toMinutes = (raw?: string | number | null): number => {
  if (!raw) return Number.POSITIVE_INFINITY; // empty values sort last
  const n = Number(raw); // "945" → 945
  const hh = Math.floor(n / 100); // 9
  const mm = n % 100; // 45
  return hh * 60 + mm; // 585
};

export const humanTime = (raw?: string | number | null): string => {
  if (!raw) return '';
  const n = Number(raw);
  const hh = Math.floor(n / 100);
  const mm = n % 100;
  const am = hh < 12;
  const hr = ((hh + 11) % 12) + 1;
  return `${hr}:${mm.toString().padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
};

/* ───────── TAB 1 – Scheduling ───────── */

export const schedulingColumnDefs: ColumnDef[] = [
  // { field: 'aspirationId', headerName: 'Aspiration ID', width: 120, hide: true },
  {
    field: 'rowNum',
    headerName: 'Donor #',
    width: 103,
    rowDrag: true,
    valueGetter: (p) => p.data?.rowNum || p.node.rowIndex + 1,
    filterValueGetter: (p) => p.data?.rowNum || p.node.rowIndex + 1,
  },
  { field: 'fertSite', headerName: 'Fert Site', width: 90 },
  { field: 'storageLocation', headerName: 'Storage Location', width: 120 },
  { field: 'donorLocation', headerName: 'Donor Location', width: 118 },
  { field: 'account', headerName: 'Account', width: 118 },
  { field: 'ranchId', headerName: 'Ranch ID', width: 100 },
  { field: 'csr', headerName: 'CSR', width: 140 },
  { field: 'donorName', headerName: 'Donor Name', width: 180 },
  {
    field: 'aspTime',
    headerName: 'Asp. Time',
    width: 158,
    valueGetter: ({ data }) => toMinutes(data?.aspTime),
    valueFormatter: ({ data }) => humanTime(data?.aspTime),
    filterValueGetter: ({ data }) => humanTime(data?.aspTime),
  },
  { field: 'csa', headerName: 'CSA', width: 80 },
  { field: 'breed', headerName: 'Donor Breed', width: 136 },
  { field: 'earTag', headerName: 'Ear Tag', width: 110 },
  { field: 'sire', headerName: 'Sire', width: 205 },
  { field: 'sireBrd', headerName: 'Sire Brd', width: 90 },
  { field: 'semenType', headerName: 'Semen Type', width: 139 },
  { field: 'parity', headerName: 'Parity', width: 102 },
  { field: 'export', headerName: 'Export', width: 106 },
  { field: 'pooled', headerName: 'Pool', width: 90 },
  {
    field: 'dsAspirationComment',
    headerName: 'DS Aspiration Comment',
    width: 120,
  },
  { field: 'maturationComment', headerName: 'Maturation Comment', width: 160 },
  { field: 'collectionCode', headerName: 'Collection Code', width: 90 },
  {
    field: 'embryoPlan',
    headerName: 'Embryo Plan',
    width: 110,
    cellRenderer: StringMapLabel,
    cellRendererParams: (params: any) => ({
      attributeName: StringMapAttributeName.EMBRYO_PLAN,
      objectTypeCode: StringMapObjectTypeCode.EMBRYO_PLAN,
      value: params.data.embryoPlan,
    }),
  },
  {
    field: 'ship',
    headerName: 'Ship',
    width: 80,
    cellRenderer: StringMapLabel,
    cellRendererParams: (params: any) => ({
      attributeName: StringMapAttributeName.SHIP,
      objectTypeCode: StringMapObjectTypeCode.SHIP,
      value: params.data.ship,
    }),
  },
  { field: 'shipmentGroupName', headerName: 'Shipment Group Name', width: 180 },
  { field: 'transfer', headerName: 'Transfer', width: 90 },
  {
    field: 'transferEmbryoComment',
    headerName: 'Transfer Embryo Comment',
    width: 180,
  },
  {
    field: 'freeze',
    headerName: 'Freeze',
    width: 80,
  },
  {
    field: 'freezeInstructions',
    headerName: 'Freeze Instructions',
    width: 160,
  },
  { field: 'schedCode', headerName: 'Sched Code', width: 100 },
  { field: 'birthDate', headerName: 'Birth Date', width: 110 },
  { field: 'procedureDate', headerName: 'Procedure Date', width: 120 },
  { field: 'programName', headerName: 'Program Name', width: 130 },
];

export const schedulingAddedColumnDefs: ColumnDef[] = [
  { field: 'donorNm', headerName: 'Donor NM', width: 100 },
  { field: 'donorPsi', headerName: 'Donor PSI', width: 100 },
  { field: 'matingPriority', headerName: 'Mating Priority', width: 120 },
  { field: 'donorComment', headerName: 'Donor Comment', width: 160 },
  { field: 'semenNaab', headerName: 'Semen NAAB', width: 120 },
  { field: 'semenSex', headerName: 'Semen Sex', width: 100 },
  { field: 'tt', headerName: 'TT', width: 70 },
  { field: 'freezeMethod', headerName: 'Freeze Method', width: 130 },
  { field: 'pregnant', headerName: 'Pregnant', width: 90 },
];

/* ───────── TAB 2 – Results ───────── */

export function useResultsColumnDefs() {
  const fixAspirationCommentWidth = useFeatureFlag(
    'fix_aspiration_comment_width',
  );
  return useMemo<ColumnDef[]>(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        width: 10,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
      },
      { field: 'account', headerName: 'Account', width: 118 },
      { field: 'ranchId', headerName: 'Ranch ID', width: 100 },
      { field: 'breed', headerName: 'Breed', width: 100 },
      { field: 'donorName', headerName: 'Donor Name', width: 180 },
      { field: 'sire', headerName: 'Sire', width: 180 },
      { field: 'sireBrd', headerName: 'Sire Brd', width: 90 },
      { field: 'semenType', headerName: 'Semen Type', width: 120 },
      { field: 'storageLocation', headerName: 'Storage Location', width: 120 },
      { field: 'donorLocation', headerName: 'Donor Location', width: 115 },
      { field: 'pooled', headerName: 'Pooled', width: 80 },
      { field: 'export', headerName: 'Export', width: 90 },
      { field: 'medTotal', headerName: 'Med/Total', width: 95 },
      { field: 'totalOocytes', headerName: 'Total Oocytes', width: 120 },
      { field: 'inMat', headerName: '# in Mat', width: 95 },
      { field: 'csr', headerName: 'CSR', width: 140 },
      {
        field: 'matTime',
        headerName: 'Mat Time',
        width: 90,
        valueGetter: ({ data }) => toMinutes(data?.matTime),
        valueFormatter: ({ data }) => humanTime(data?.matTime),
        filterValueGetter: ({ data }) => humanTime(data?.matTime),
      },
      fixAspirationCommentWidth
        ? {
            field: 'aspirationComment',
            headerName: 'Aspiration Comment',
            width: 190,
          }
        : {
            field: 'aspirationComment',
            headerName: 'Aspiration Comment',
            flex: 1,
          },
      { field: 'poolNum', headerName: 'Pool Num', width: 90 },
      { field: 'ship', headerName: 'Ship', width: 80 },
      {
        field: 'shipmentGroupName',
        headerName: 'Shipment Group ame',
        width: 180,
      },
      { field: 'transfer', headerName: 'Transfer', width: 90 },
      {
        field: 'transferEmbryoComment',
        headerName: 'Transfer Embryo Comment',
        width: 200,
      },
      { field: 'freeze', headerName: 'Freeze', width: 80 },
      {
        field: 'freezeInstructions',
        headerName: 'Freeze Instructions',
        width: 160,
      },
      { field: 'schedCode', headerName: 'Sched Code', width: 100 },
      { field: 'fertSite', headerName: 'Fert Site', width: 90 },
      {
        field: 'maturationComment',
        headerName: 'Maturation Comment',
        width: 160,
      },
      {
        field: 'opuTechnician',
        headerName: 'OPU Technician',
        width: 130,
      },
      {
        field: 'ivmTechnician',
        headerName: 'IVM Technician',
        width: 130,
      },
      { field: 'gradeAbc', headerName: 'Grade ABC', width: 100 },
      { field: 'gradeD', headerName: 'Grade D', width: 90 },
      { field: 'gradeE', headerName: 'Grade E', width: 90 },
      { field: 'dead', headerName: 'Dead', width: 90 },
      { field: 'revisionNumber', headerName: 'Revision Number', width: 90 },
      { field: 'revisionComment', headerName: 'Revision Comment', width: 230 },
      //hidden fields
      {
        field: 'collectionCode',
        headerName: 'Collection Code',
        width: 90,
        hide: true,
      },
      {
        field: 'embryoPlan',
        headerName: 'Embryo Plan',
        width: 110,
      },
      {
        field: 'ship',
        headerName: 'Ship',
        width: 80,
        hide: true,
      },
      {
        field: 'shipmentGroupName',
        headerName: 'Shipment Group Name',
        width: 180,
        hide: true,
      },
      { field: 'transfer', headerName: 'Transfer', width: 90, hide: true },
      {
        field: 'transferEmbryoComment',
        headerName: 'Transfer Embryo Comment',
        width: 180,
        hide: true,
      },
      {
        field: 'freezeInstructions',
        headerName: 'Freeze Instructions',
        width: 160,
        hide: true,
      },
      { field: 'schedCode', headerName: 'Sched Code', width: 100, hide: true },
      { field: 'birthDate', headerName: 'Birth Date', width: 110, hide: true },
      {
        field: 'procedureDate',
        headerName: 'Procedure Date',
        width: 120,
        hide: true,
      },
      {
        field: 'programName',
        headerName: 'Program Name',
        width: 130,
        hide: true,
      },
      {
        field: 'donorScheduleId',
        headerName: 'Donor Schedule ID',
        width: 130,
        hide: true,
      },
    ],
    [fixAspirationCommentWidth],
  );
}

export const getResultsPartnershipColumnDef = (t: TFunction): ColumnDef => ({
  field: 'partnership',
  headerName: t('chuteScheduler.partnership'),
  width: 300,
  cellRenderer: PartnershipCellRenderer,
  valueGetter: ({ data }) => formatPartnership(data?.partnership),
  filterValueGetter: ({ data }) => formatPartnership(data?.partnership),
});

export const excelStyles: ExcelStyle[] = [
  {
    id: 'bg-tog-light-purple',
    interior: {
      color: '#E6E6FA',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-light-green',
    interior: {
      color: '#90EE90',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-pink',
    interior: {
      color: '#f7b6c0',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-red',
    interior: {
      color: '#d71925',
      pattern: 'Solid',
    },
    font: {
      color: '#FFFFFF',
    },
  },
  {
    id: 'bg-tog-yellow',
    interior: {
      color: '#ffff00',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-maroon',
    interior: {
      color: '#800000',
      pattern: 'Solid',
    },
    font: {
      color: '#FFFFFF',
    },
  },
  {
    id: 'bg-tog-light-blue-shaded',
    interior: {
      color: '#add8e6',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-orange',
    interior: {
      color: '#ffa500',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-light-pink',
    interior: {
      color: '#f7b6c0',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-light-orange',
    interior: {
      color: '#ffd580',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-teal',
    interior: {
      color: '#008080',
      pattern: 'Solid',
    },
    font: {
      color: '#FFFFFF',
    },
  },
  {
    id: 'bg-tog-light-brown',
    interior: {
      color: '#d2691e',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-light-gray',
    interior: {
      color: '#b3b0b0',
      pattern: 'Solid',
    },
  },
  {
    id: 'bg-tog-light-yellow',
    interior: {
      color: '#F3F3BD',
      pattern: 'Solid',
    },
  },
  {
    id: 'tog-header-black',
    interior: {
      color: '#000000',
      pattern: 'Solid',
    },
    font: {
      color: '#FFFFFF',
      bold: true,
    },
    alignment: {
      horizontal: 'Center',
      vertical: 'Center',
    },
  },
];
